// src/pages/AdminLogin.jsx
import { useState, useEffect } from "react";
import axios from "axios";
import "../css/login.css";
import Logo from "../assets/logo.png";
import Swal from "sweetalert2";
import { Link, useNavigate } from "react-router-dom";

const AdminLogin = () => {
  const [userName, setUserName] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd]   = useState(false);
  const [error, setError]       = useState("");
  const [loading, setLoading]   = useState(false);
  const [siteName, setSiteName] = useState("Infraconsoft");
  const [siteLogo, setSiteLogo] = useState("");


  const navigate = useNavigate();

  // Normalize server responses to friendly text
  function messageFromResponse(data) {
    if (!data) return "Login failed.";
    if (data.success) return "";
    // Prefer server-provided message when present
    if (data.message) return data.message;

    switch (data.code) {
      case "MISSING_FIELDS":
        return "Username and password are required.";
      case "TENANT_SUSPENDED":
        return "This tenant is suspended. Please contact your administrator.";
      case "LICENSE_EXPIRED":
        return "License expired. Please contact your administrator.";
      case "ADMIN_INACTIVE":
        return "Your admin ID is inactive. Please contact your administrator.";
      case "INVALID_CREDENTIALS":
        return "Invalid username or password.";
      case "LICENSE_NOT_INITIALIZED":
        return "License not initialized for this tenant. Please contact support.";
      case "SERVER_ERROR":
        return "Server error. Please try again.";
      default:
        return "Login failed. Please try again.";
    }
  }

  useEffect(() => {
  const fetchData = async () => {
    Swal.fire({
      title: "Loading",
      html: `
        <div style="width: 100%; background: #e0e0e0; border-radius: 5px;">
          <div style="height: 10px; width: 0%; background: linear-gradient(to right, #4facfe, #00f2fe); animation: progressBar 2s linear infinite;"></div>
        </div>
        <p style="margin-top: 10px; font-size: 14px;">Fetching branches & branding…</p>
      `,
      showConfirmButton: false,
      allowOutsideClick: false,
      didOpen: () => {
        const style = document.createElement("style");
        style.innerHTML = `
          @keyframes progressBar {
            0% { width: 0% }
            50% { width: 100% }
            100% { width: 0% }
          }`;
        document.head.appendChild(style);
      },
    });

    try {
      const { data: s } = await axios.get("https://rfs2.infraconsoft.net.in/api/siteinfo", {
        timeout: 10000,
      });

      if (s?.ok) {
        if (s.name) setSiteName(s.name);

        if (s.logo) {
          // Normalize to absolute https URL to avoid mixed-content blocks
          let url = s.logo.trim();

          // If it's relative, prefix your domain
          if (!/^https?:\/\//i.test(url)) {
            url = `https://rfs2.infraconsoft.net.in/${url.replace(/^\/+/, "")}`;
          }

          // If it's http, force https
          url = url.replace(/^http:\/\//i, "https://");

          setSiteLogo(url);
        }
      }
    } catch (err) {
      console.error("Init load error:", err);
      Swal.fire({ icon: "warning", title: "Some data failed to load", text: "Using defaults." });
    } finally {
      Swal.close();
    }
  };

  fetchData();
}, []);


  const handleLogin = async (event) => {
    event.preventDefault();
    setError("");

    if (!userName.trim() || !password.trim()) {
      setError("Please enter username and password.");
      return;
    }

    try {
      setLoading(true);

      const { data } = await axios.post("https://rfs2.infraconsoft.net.in/api/admin_login", {
        username: userName.trim(),
        password: password,
      });

      if (data?.success) {
        const u = data.user || {};
        const license = data.license || {};
        const modules = Array.isArray(data.modules) ? data.modules : [];

        // Persist what the app needs
        localStorage.setItem(
          "nissi_admin",
          JSON.stringify({
            id: u.id,
            username: u.username,
            company_name: u.company_name,
            num_licenses: u.num_licenses,  // your bitmask column
            num_users: u.num_users,        // allowed users
            is_active: u.is_active ? 1 : 0
          })
        );
        localStorage.setItem("modules", JSON.stringify(modules));
        localStorage.setItem("license", JSON.stringify(license));
        localStorage.removeItem("username"); // cleanup legacy

        navigate("/admin/cd");
      } else {
        setError(messageFromResponse(data));
      }
    } catch (err) {
      const apiMsg = err?.response?.data?.message;
      const apiCode = err?.response?.data?.code;
      const msg = apiMsg || messageFromResponse({ code: apiCode }) || err.message || "Login failed.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login">
      <div className="login_box">
        <div className="left">
        
          <div className="contact">
            <form onSubmit={handleLogin} noValidate>
              <div className="margin-div">

                   <div className="product-brand">
                          <img src={Logo} alt="App logo" className="product-logo" />
                        </div>
                <h3 className="hd">{siteName ? `${siteName} - Login` : "Admin Login"}</h3>

                {error && (
                  <p
                    role="alert"
                    aria-live="assertive"
                    style={{ color: "red", marginBottom: 10, whiteSpace: "pre-wrap" }}
                  >
                    {error}
                  </p>
                )}

                <input
                  type="text"
                  placeholder="USERNAME"
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                  autoComplete="username"
                />

                <div style={{ position: "relative" }}>
                  <input
                    type={showPwd ? "text" : "password"}
                    placeholder="PASSWORD"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    style={{ paddingRight: 44 }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd((v) => !v)}
                    title={showPwd ? "Hide password" : "Show password"}
                    style={{
                      position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
                      background: "transparent", border: "none", cursor: "pointer", opacity: 0.8
                    }}
                    aria-label={showPwd ? "Hide password" : "Show password"}
                  >
                    {showPwd ? "🙈" : "👁️"}
                  </button>
                </div>
              </div>

              <button type="submit" className="submit" disabled={loading}>
                {loading ? "Logging in…" : "Login"}
              </button>
            </form>
          </div>
        </div>

       <div
  className={`right ${siteLogo ? "right-bg" : ""}`}
  style={siteLogo ? { backgroundImage: `url(${siteLogo})` } : undefined}
>
  {!siteLogo && (
    <div className="brand">
      <h2 className="brand-name">{siteName}</h2>
    </div>
  )}
</div>

      </div>
    </div>
  );
};

export default AdminLogin;
